import type {ReactElement} from 'react';
import {useId, useState} from 'react';
import {resolveChatMediaUrl} from '@Libraries/ai-chat.library';
import {MediaItemSizeType} from '@Libraries/s3-library';
import {Text, TextSize} from '@Components/text';
import type {EntityDraftCardProps} from './entity-draft-card.types';
import styles from './entity-draft-card.module.scss';

const DESCRIPTION_TRUNCATE_LENGTH = 180;
const META_SEPARATOR = '  ·  ';

export function EntityDraftCard({badge, meta, title, description, mediaUrl}: EntityDraftCardProps): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);
  const descriptionId = useId();

  const metaLine = meta.join(META_SEPARATOR);
  const isTruncatable = typeof description === 'string' && description.length > DESCRIPTION_TRUNCATE_LENGTH;

  const renderBadge = (): ReactElement | null => {
    if (badge === undefined || badge === '') {
      return null;
    }
    return <Text val={badge} size={TextSize.SM} bold className={styles.badge} />;
  };

  const renderTitle = (): ReactElement | null => {
    if (title === undefined || title === '') {
      return null;
    }
    return <Text val={title} size={TextSize.MD} bold className={styles.title} />;
  };

  const renderToggle = (): ReactElement | null => {
    if (!isTruncatable) {
      return null;
    }

    return (
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={isExpanded}
        aria-controls={descriptionId}
        onClick={(): void => {
          setIsExpanded((prev) => !prev);
        }}
      >
        {isExpanded ? window.i18next.t('pmwjs_show_less') : window.i18next.t('pmwjs_show_more')}
      </button>
    );
  };

  const renderDescription = (): ReactElement | null => {
    if (description === undefined || description === '') {
      return null;
    }
    const shownText = isTruncatable && !isExpanded ? `${description.slice(0, DESCRIPTION_TRUNCATE_LENGTH).trimEnd()}… ` : `${description} `;
    return (
      <p id={descriptionId} className={styles.description}>
        {shownText}
        {renderToggle()}
      </p>
    );
  };

  const renderMedia = (): ReactElement | null => {
    if (mediaUrl === undefined || mediaUrl === '') {
      return null;
    }
    return (
      <div className={styles.media}>
        <img className={styles.mediaImage} src={resolveChatMediaUrl(mediaUrl, MediaItemSizeType.HIGH_RES)} alt={title ?? badge ?? ''} />
      </div>
    );
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <Text val={metaLine} size={TextSize.MD} bold className={styles.meta} />
        {renderBadge()}
      </div>
      <div className={styles.divider} />
      {renderTitle()}
      {renderDescription()}
      {renderMedia()}
    </div>
  );
}
